import { AuthStorage } from "utils/auth-storage";
import { useCallback, useEffect, useState } from "react";
import { ProjectServices } from "apis/Services/ProjectService";
import { SideBarEndService } from "apis/Services/SideBarService";

function pickFirstNonEmpty(...values) {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    if (typeof value === "string" && value.trim() === "") {
      continue;
    }

    return value;
  }

  return "";
}

function pickFirstDefined(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined) {
      return value;
    }
  }

  return null;
}

// Pulls the latest schedule array out of the supported procurement response shapes.
function extractSchedule(response) {
  const scheduleCandidates = [
    response?.projectDetails?.schedule,
    response?.content?.projectDetails?.schedule,
    response?.schedule,
    response?.content?.schedule,
  ];

  return (
    scheduleCandidates.find(
      (candidate) => Array.isArray(candidate) && candidate.length > 0,
    ) ||
    scheduleCandidates.find((candidate) => Array.isArray(candidate)) ||
    []
  );
}

function extractProjectHeader(response) {
  return {
    projectName: pickFirstNonEmpty(
      response?.projectInfo,
      response?.projectDetails?.projectInfo,
      response?.content?.projectInfo,
      response?.content?.projectDetails?.projectInfo,
    ),
    projectAddress: pickFirstNonEmpty(
      response?.projectAddress,
      response?.projectDetails?.projectAddress,
      response?.content?.projectAddress,
      response?.content?.projectDetails?.projectAddress,
    ),
    approvalTime: pickFirstDefined(
      response?.approvalTime,
      response?.projectDetails?.approvalTime,
      response?.content?.approvalTime,
      response?.content?.projectDetails?.approvalTime,
    ),
    reviewTime: pickFirstDefined(
      response?.reviewTime,
      response?.projectDetails?.reviewTime,
      response?.content?.reviewTime,
      response?.content?.projectDetails?.reviewTime,
    ),
  };
}

// Reads the optional submittal picker data returned with the procurement log.
function extractSubmittalsDropDown(response) {
  const candidates = [
    response?.submittalsDropDown,
    response?.content?.submittalsDropDown,
  ];

  return (
    candidates.find(
      (candidate) => candidate && typeof candidate === "object",
    ) || {}
  );
}

// Loads the procurement log data and exposes the pieces the page needs to render.
export function useProcurementData(company_id, project_id) {
  const [isLoading, setIsLoading] = useState(true);
  const [alignedData, setAlignedData] = useState(null);
  const [rowsData, setRowsData] = useState(null);
  const [divisions, setDivisions] = useState(null);
  const [subdivisions, setSubdivisions] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [lastUsedView, setLastUsedView] = useState(null);
  const [viewNames, setViewNames] = useState([]);
  const [accessLevel, setAccessLevel] = useState(null);
  const [wareHouseAccessLevel, setWareHouseAccessLevel] = useState(null);
  const [warehouseSubscription, setWarehouseSubscription] = useState(null);
  const [workingDays, setWorkingDays] = useState({});
  const [nonWorkingDays, setNonWorkingDays] = useState([]);
  const [usersInfo, setUsersInfo] = useState([]);
  const [settingsInfo, setSettingsInfo] = useState({});
  const [itemsPendingApprovals, setItemsPendingApprovals] = useState([]);
  const [tasksDict, setTasksDict] = useState({});
  const [dependenciesDict, setDependenciesDict] = useState({});
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [projectName, setProjectName] = useState("");
  const [projectAddress, setProjectAddress] = useState("");
  const [approvalTime, setApprovalTime] = useState(null);
  const [reviewTime, setReviewTime] = useState(null);
  const [submittalsDropDown, setSubmittalsDropDown] = useState({});

  // Triggers a fresh procurement-log fetch after view or data changes.
  const reloadData = useCallback(() => {
    setReloadToken((previousToken) => previousToken + 1);
  }, []);

  useEffect(() => {
    if (!company_id || !project_id) return;

    let isActive = true;
    setIsLoading(true);
    setError(null);
    setDependenciesDict({});
    setSubmittalsDropDown({});

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");

    const ReqObj = {
      company: company_id,
      project: project_id,
      code,
      loginEmail,
      filtersInfo: {
        filteredColumns: {},
        assignedTo: [],
        koncurentStatus: [],
      },
    };
    const dependencyRequest = {
      company_id,
      id: project_id,
      code,
      loginEmail,
    };

    Promise.allSettled([
      ProjectServices.procurementLogLoadFromDB(ReqObj),
      SideBarEndService.getProcurementLogRowDependencies(dependencyRequest),
    ])
      .then(([procurementResult, dependenciesResult]) => {
        if (!isActive) {
          return;
        }

        if (procurementResult.status === "fulfilled") {
          const res = procurementResult.value;
          console.log("full res:", res);
          const {
            projectName: nextProjectName,
            projectAddress: nextProjectAddress,
            approvalTime: nextApprovalTime,
            reviewTime: nextReviewTime,
          } = extractProjectHeader(res);
          const level = parseInt(res?.usersInfo?.[0]?.accessLevel, 10);
          const nextWareHouseAccessLevel = parseInt(
            res?.usersInfo?.[0]?.wareHouseAccessLevel,
            10,
          );
          const nextWarehouseSubscription = parseInt(
            res?.usersInfo?.[0]?.warehouseSubscription,
            10,
          );
          setAccessLevel(isNaN(level) ? null : level);
          setWareHouseAccessLevel(
            isNaN(nextWareHouseAccessLevel) ? null : nextWareHouseAccessLevel,
          );
          setWarehouseSubscription(
            isNaN(nextWarehouseSubscription) ? null : nextWarehouseSubscription,
          );
          setAlignedData(res?.viewsInfo?.alignedData ?? null);
          setLastUsedView(res?.viewsInfo?.lastUsedView ?? null);
          setViewNames(res?.viewsInfo?.viewNames ?? []);
          setDivisions(res?.content?.divisionsList ?? []);
          setSubdivisions(res?.content?.subdivisionsList ?? []);
          setRowsData(res?.content?.submittalsList ?? []);
          setSubmittalsDropDown(extractSubmittalsDropDown(res));
          setSchedule(extractSchedule(res));
          setWorkingDays(res?.content?.workingDays ?? res?.workingDays ?? {});
          setNonWorkingDays(
            res?.content?.nonWorkingDays ?? res?.nonWorkingDays ?? [],
          );
          setUsersInfo(res?.usersInfo ?? []);
          setProjectName(nextProjectName);
          setProjectAddress(nextProjectAddress);
          setApprovalTime(nextApprovalTime);
          setReviewTime(nextReviewTime);
          setSettingsInfo(res?.settingsInfo?.[0] ?? {});
          setItemsPendingApprovals(res?.itemsPendingApprovals ?? []);
          setTasksDict(res?.tasksDict ?? {});
        } else {
          console.log(procurementResult.reason);
          setError(procurementResult.reason);
        }

        if (dependenciesResult.status === "fulfilled") {
          setDependenciesDict(dependenciesResult.value?.dependenciesDict || {});
        } else {
          console.error(
            "Failed to load procurement dependencies",
            dependenciesResult.reason,
          );
          setDependenciesDict({});
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [company_id, project_id, reloadToken]);

  return {
    isLoading,
    alignedData,
    rowsData,
    divisions,
    subdivisions,
    schedule,
    lastUsedView,
    viewNames,
    accessLevel,
    wareHouseAccessLevel,
    warehouseSubscription,
    workingDays,
    nonWorkingDays,
    usersInfo,
    settingsInfo,
    itemsPendingApprovals,
    tasksDict,
    dependenciesDict,
    projectName,
    projectAddress,
    approvalTime,
    reviewTime,
    submittalsDropDown,
    error,
    reloadData,
  };
}
